import type { GridApi, GridState } from "ag-grid-community";
import type { ResponsesGridRow } from "./types";

/**
 * The carrier column is filtered (not column-hidden) to hide carriers, so its
 * colId is excluded from the plan-group set and its set filter carries the
 * carrier hides.
 */
const CARRIER_NAME_COL_ID = "carrierName";

/** The shared, per-RFP hidden set persisted to the responses-visibility store. */
export interface ResponsesHiddenSet {
  hiddenCarrierIds: string[];
  hiddenPlanGroupIds: string[];
}

type ResponsesVisibilityGridState = Pick<
  GridState,
  "columnVisibility" | "filter"
>;

/**
 * Projects a stored hidden set onto the AG-Grid state facets that express it:
 * hidden coverages become hidden columns (colId = planGroupId), and hidden
 * carriers become a set filter on the carrier column that keeps only the
 * visible carriers' names.
 */
export const toResponsesVisibilityGridState = (
  { hiddenCarrierIds, hiddenPlanGroupIds }: ResponsesHiddenSet,
  rows: ResponsesGridRow[],
): ResponsesVisibilityGridState => {
  // The carrier column is hidden via its set filter, never column-hidden, so
  // drop it here even if a stale/bad store entry names it — hiding the grid's
  // primary/filter column would break the grid.
  const hiddenColIds = hiddenPlanGroupIds.filter(
    (id) => id !== CARRIER_NAME_COL_ID,
  );
  const columnVisibility =
    hiddenColIds.length > 0 ? { hiddenColIds } : undefined;

  const hiddenCarrierIdSet = new Set(hiddenCarrierIds);
  const visibleCarrierNames = rows
    .filter((row) => !hiddenCarrierIdSet.has(row.carrierId))
    .map((row) => row.carrierName);

  const filter =
    hiddenCarrierIds.length > 0
      ? {
          filterModel: {
            [CARRIER_NAME_COL_ID]: {
              filterType: "set",
              values: visibleCarrierNames,
            },
          },
        }
      : undefined;

  return { columnVisibility, filter };
};

/**
 * Reads the current hidden set back out of the live grid. Hidden carriers are
 * the rows the carrier filter excludes (all rows minus the post-filter rows),
 * so the derivation is id-based and independent of the filter's UI shape.
 */
export const deriveResponsesVisibility = (
  api: GridApi<ResponsesGridRow>,
): ResponsesHiddenSet => {
  const allCarrierIds: string[] = [];
  api.forEachNode((node) => {
    if (node.data) allCarrierIds.push(node.data.carrierId);
  });

  const visibleCarrierIds = new Set<string>();
  api.forEachNodeAfterFilter((node) => {
    if (node.data) visibleCarrierIds.add(node.data.carrierId);
  });

  const hiddenCarrierIds = allCarrierIds.filter(
    (id) => !visibleCarrierIds.has(id),
  );

  const hiddenColIds = api.getState().columnVisibility?.hiddenColIds ?? [];
  const hiddenPlanGroupIds = hiddenColIds.filter(
    (id) => id !== CARRIER_NAME_COL_ID,
  );

  return { hiddenCarrierIds, hiddenPlanGroupIds };
};

/** Order-insensitive equality for two hidden sets. */
export const responsesHiddenSetsEqual = (
  a: ResponsesHiddenSet,
  b: ResponsesHiddenSet,
): boolean => {
  const sameMembers = (x: string[], y: string[]): boolean => {
    if (x.length !== y.length) return false;
    const ySet = new Set(y);
    return x.every((value) => ySet.has(value));
  };

  return (
    sameMembers(a.hiddenCarrierIds, b.hiddenCarrierIds) &&
    sameMembers(a.hiddenPlanGroupIds, b.hiddenPlanGroupIds)
  );
};
